import { ThemeProvider, useMediaQuery } from '@mui/material';
import { createContext, FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { MATERIAL_DARK_THEME, MATERIAL_THEME } from '../../theme';

export type ThemeVariant = 'light' | 'dark' | 'system';

interface ThemeSwitcherState {
  switchTheme: (newTheme: ThemeVariant) => void;
  selectedVariant: ThemeVariant;
}

const ThemeSwitcherContext = createContext<ThemeSwitcherState>({
  switchTheme: () => undefined,
  selectedVariant: 'system',
});

const SELECTED_THEME_KEY = 'themeSelectedByUser';

export const useThemeSwitcher = (): ThemeSwitcherState => useContext<ThemeSwitcherState>(ThemeSwitcherContext);

export interface Props {
  children: React.ReactNode;
}

export const ThemeSwitcherProvider: FC<Props> = ({ children }) => {
  const prefersLightMode = useMediaQuery('(prefers-color-scheme: light)');

  const [selectedVariant, setSelectedVariant] = useState<ThemeVariant>(
    (localStorage.getItem(SELECTED_THEME_KEY) as ThemeVariant) || 'system',
  );

  const handleThemeSwitch = useCallback((newTheme: ThemeVariant) => {
    setSelectedVariant(newTheme);
    localStorage.setItem(SELECTED_THEME_KEY, newTheme);
  }, []);

  const resolvedVariant = selectedVariant === 'system' ? (prefersLightMode ? 'light' : 'dark') : selectedVariant;
  const actualTheme = useMemo(() => (resolvedVariant === 'light' ? MATERIAL_THEME : MATERIAL_DARK_THEME), [resolvedVariant]);

  // keep in sync with the pre-hydration class set by the inline script in index.html
  useEffect(() => {
    document.documentElement.classList.toggle('theme-dark', resolvedVariant === 'dark');
  }, [resolvedVariant]);

  return (
    <ThemeSwitcherContext.Provider
      value={useMemo(
        () => ({
          switchTheme: handleThemeSwitch,
          selectedVariant,
        }),
        [handleThemeSwitch, selectedVariant],
      )}
    >
      <ThemeProvider theme={actualTheme}>{children}</ThemeProvider>
    </ThemeSwitcherContext.Provider>
  );
};
